import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

// Shown once, right after a successful signup (Register.jsx navigates here).
const Welcome = () => {
  const { t } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) return null;
  // Only reachable straight after signup; anyone else goes Home.
  if (!user || !location.state?.justRegistered) return <Navigate to="/" replace />;

  const isSeller = user.role === "seller";
  const firstName = (user.name || "").trim().split(/\s+/)[0];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F6F7F4]">
      <div data-testid="welcome-screen" className="w-full max-w-sm bg-white rounded-2xl border border-[#E5E7EB] p-6 text-center">
        <div className="w-20 h-20 rounded-full bg-[#E8F3EC] flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-12 h-12 text-[#123A2C]" />
        </div>
        <h1 data-testid="welcome-title" className="text-2xl font-extrabold text-[#14251F] mb-1">
          {t("welcome.title").replace("{name}", firstName)}
        </h1>
        <p className="text-base text-[#14251F] mb-5">{t("welcome.subtitle")}</p>

        <div className="rounded-xl bg-[#F6F7F4] p-3 mb-4 text-left">
          <p className="text-xs text-[#6B7280] mb-1">{t("welcome.loginWith")}</p>
          <p data-testid="welcome-phone" className="text-lg font-bold text-[#14251F] tracking-wide">{user.phone}</p>
        </div>

        <p data-testid="welcome-tip" className="text-sm text-[#6B7280] mb-6 text-left">
          {t(isSeller ? "welcome.sellerTip" : "welcome.buyerTip")}
        </p>

        <button
          data-testid="welcome-primary-btn"
          onClick={() => navigate(isSeller ? "/dashboard/add" : "/market", { replace: true })}
          className="w-full py-3.5 rounded-xl bg-[#123A2C] text-white font-semibold text-base hover:bg-[#0D2C21] transition-colors"
        >
          {t(isSeller ? "welcome.addFirst" : "welcome.browse")}
        </button>
        <button
          data-testid="welcome-home-btn"
          onClick={() => navigate("/", { replace: true })}
          className="mt-3 w-full py-2.5 text-sm font-medium text-[#123A2C] underline"
        >
          {t("welcome.goHome")}
        </button>
      </div>
    </div>
  );
};

export default Welcome;
